import _ from 'lodash';
import { flow, flatMap, toPairs, map, groupBy, head, filter, concat, sortBy, values } from 'lodash/fp';

import filterValidDesignText from './utils/filterValidDesignText';
import makeCleanId from './utils/makeCleanId';
import getItemMeasurement from './utils/getItemMeasurement';
import findImage from './utils/findImage';
import makeItemFilter from './utils/makeItemFilter';
import messages from './messages/sinkMessages';
function processCondition(cond) {
  if (cond.thickness) {
    const { min, max } = cond.thickness;
    return ({ thickness }) => min <= thickness && thickness <= max;
  } else if (cond.depth) {
    const f = ({ depth }) => depth === cond.depth;
    f.depth = cond.depth;
    return f;
  } else {
    console.error('Unknown condition', cond); // eslint-disable-line no-console
    return () => false;
  }
}

export const sinkTypes = {
  wood: 'woodImage',
  dark: 'darkImage',
  light: 'lightImage',
};

function getSize(item) {
  const diameter = getItemMeasurement(item, 'Diameter');
  const depth = getItemMeasurement(item, 'Depth');
  const length = getItemMeasurement(item, 'Length');
  const width = getItemMeasurement(item, 'Width');
  const frontToBack = getItemMeasurement(item, 'Front to back');
  if (diameter) {
    const size = Math.PI * (diameter / 2) * (diameter / 2);
    return size;
  } if (length && frontToBack) {
    const size = length * frontToBack;
    return size;
  } if (width && depth) {
    const size = width * depth;
    return size;
  } else {
    const size = length * depth;
    return size;
  }
}

function getWidth(item) {
  const diameter = getItemMeasurement(item, 'Diameter');
  const length = getItemMeasurement(item, 'Length');
  const width = getItemMeasurement(item, 'Width');

  return diameter || length || width;
}

const designRegExp = /(.*)(?:f|for) custom made worktop (.*)/;

const imgFinder = (size, ImageType) => flow(map((s) => findImage(s.item, size, { ImageType })), head);
const imgFinderWithSortNo = (size, ImageType, SortNo) => flow(map((s) => findImage(s.item, size, { ImageType, SortNo })), head);

function isUnderGluedSink(item) {
  return item.ValidDesignTextGlobal && item.ValidDesignTextGlobal.match(designRegExp) && item.ItemType === 'ART';
}

export const fasteningTypes = {
  underGlued: 'underGlued',
  fitBetween: 'fitBetween',
  topMounted: 'topMounted',
};

function fasteningType(item) {
  if (item.ValidDesignTextGlobal.match(designRegExp)) {
    return fasteningTypes.underGlued;
  }

  if (item.ProductTypeNameGlobal.indexOf('onset') >= 0) {
    return fasteningTypes.fitBetween;
  }

  return fasteningTypes.topMounted;
}

function createSink(sink, item, fastening) {
  const [,, worktopMaterialId] = item.ValidDesignTextGlobal.match(designRegExp) || [];
  return {
    ...sink,
    worktopMaterialId,
    fastening,
  };
}

function createSinksWithUnderGluedOperation(sink, underGluedMaterials, sinkAllowsUnderGlued) {
  const topMounted = {
    ...sink,
    fastening: fasteningTypes.topMounted,
  };

  if (sinkAllowsUnderGlued) {
    return [
      topMounted,
      ...underGluedMaterials.map((worktopMaterialId) => ({
        ...sink,
        fastening: fasteningTypes.underGlued,
        worktopMaterialId,
      })),
    ];
  }
  return [topMounted];
}

function createManuallyConfiguredSinksWithMoreThanOneFastening(sink, fastenings, item) {
  const [,, worktopMaterialId] = item.ValidDesignTextGlobal.match(designRegExp) || [];
  return [...fastenings.map((currFastening) => ({
    ...sink,
    fastening: currFastening,
    worktopMaterialId,
  })),
  ];
}

const compareAndFilterMaterials = (materialsUseDecentralizedSinkRange, materialsWithUnderGluing) => {
  const filteredMaterials = [...new Set([...materialsUseDecentralizedSinkRange, ...materialsWithUnderGluing])];
  return filteredMaterials;
};

export function generateSinks({ sinkMaterials, sinkModels, useImperialUnitMeasurementsInTheUI }, items, materials = []) {
  const materialsWithUnderGluing = materials.filter((material) => material.operations.underGlued).map((material) => material.id);
  const materialsUseDecentralizedSinkRange = materials.filter((material) => material.decentralizedSinkOptions).map((material) => material.id);
  const useImperialUnits = useImperialUnitMeasurementsInTheUI;
  function findItem(conditions) {
    const c = _.toPairs(conditions);
    return items.find((item) => c.every(([key, value]) => value === item[key]));
  }
  function toSinkItem(conditions, fastening, worktopMaterialId) {
    const item = findItem(conditions);
    if (!item) return [];
    let ItemMeasureReferenceText;

    if (useImperialUnits) {
      ItemMeasureReferenceText = item.ItemMeasureReferenceTextImperial;
    } else {
      ItemMeasureReferenceText = item.ItemMeasureReferenceTextMetric;
    }
    const measurement = ItemMeasureReferenceText;
    const displayName = `${item.ProductName} ${measurement}`;
    return [{
      displayName,
      item,
      worktopMaterialId,
      fastening,
    }];
  }
  const sinkConfigurations = sinkModels.configuration || [];
  const sinkRequiresTapHole = makeItemFilter(sinkModels.requiresTapHole);
  const sinkHasDrainer = makeItemFilter(sinkModels.hasDrainer);
  const sinkAllowsUnderGlued = sinkModels.allowsUnderGlued ? makeItemFilter(sinkModels.allowsUnderGlued) : () => true;
  const partOfPredefinedOption = flow(flatMap((option) => _.values(option.underGlued).concat([option.topMounted])), map('ItemNo'))(sinkModels.options);

  const predefinedOptions = _.flatMap(sinkModels.options, (option) => {
    const topMountedSinks = option.topMounted ? toSinkItem(option.topMounted, 'topMounted') : [];
    const underGlued = option.underGlued ? flow(toPairs, flatMap(([worktopMaterialId, i]) => toSinkItem(i, 'underGlued', worktopMaterialId)))(option.underGlued) : [];
    const similarSinks = topMountedSinks.concat(underGlued);

    if (similarSinks.length === 0) return [];

    const displayImage = imgFinder('S3', 'Picture Single');
    const displayImageLarge = imgFinder('S4', 'Picture Single');
    const wood = imgFinderWithSortNo('S4', 'Functional Product Picture', 2);
    const light = imgFinderWithSortNo('S4', 'Functional Product Picture', 3);
    const dark = imgFinderWithSortNo('S4', 'Functional Product Picture', 4);

    return [{
      id: makeCleanId(option.id),
      requiresTapHole: option.requiresTapHole,
      hasDrainer: option.hasDrainer,
      displayName: option.displayName || similarSinks[0].displayName,
      displayImage: option.displayImage || displayImage(similarSinks),
      displayImageLarge: option.displayImageLarge || displayImageLarge(similarSinks),
      source: similarSinks, // only for debug
      size: getSize(similarSinks[0].item),
      ProductName: similarSinks[0].item.ProductName,
      fastenings: _.groupBy(similarSinks, ((s) => s.fastening)),
      woodImage: wood(topMountedSinks),
      lightImage: light(topMountedSinks),
      darkImage: dark(topMountedSinks),
      isManuallyConfigured: true,
    }];
  });

  return sinkMaterials.map((sinkRules) => {
    const productNameSortOrder = _.fromPairs(sinkRules.products.map((product, index) => [product, index]));
    const productNameThenSize = [({ ProductName }) => productNameSortOrder[ProductName], ({ size }) => size];
    let ItemMeasureReferenceText;
    return {
      id: sinkRules.id,
      displayName: messages[sinkRules.id] || { id: `cwt.sinks.${sinkRules.id}` },
      displayImage: sinkRules.displayImage,
      displayImageLarge: sinkRules.displayImageLarge,
      models: flow(
      filter((item) =>
        !partOfPredefinedOption.includes(item.ItemNo) &&
        sinkRules.products.includes(item.ProductNameGlobal) &&
        item.ProductTypeNameGlobal.indexOf('sink') >= 0 &&
        (isUnderGluedSink(item) || item.ItemType === 'SPR')
      ),
      filterValidDesignText('sink', sinkRules.id),
      flatMap((item) => { 
        const configuration = sinkConfigurations.find((config) => config.ItemNo === item.ItemNo);

        let configListOfFastenings;
        if (configuration && configuration.fastening) {
          if (Array.isArray(configuration.fastening) || configuration.fastening.size > 1) {
            configListOfFastenings = configuration.fastening.map((fastening) => {
              return fastening;
            });
          }
        }
        const width = getWidth(item);
        if (useImperialUnits) {
          ItemMeasureReferenceText = item.ItemMeasureReferenceTextImperial;
        } else {
          ItemMeasureReferenceText = item.ItemMeasureReferenceTextMetric;
        }
        if (item.ValidDesignTextGlobal && item.ValidDesignTextGlobal.includes(' custom made worktop thick veneer')) {
          item.ValidDesignTextGlobal = item.ValidDesignTextGlobal.replace('thick veneer', 'wood');
          item.ValidDesignText = item.ValidDesignText.replace('thick veneer', 'wood');
        }
        const [, valdDesignText] = item.ValidDesignTextGlobal.match(designRegExp) || [];
        const designText = valdDesignText || item.ValidDesignTextGlobal;
        const measurement = ItemMeasureReferenceText;
        const displayName = `${item.ProductName} ${measurement}`;
        const sinkId = `${item.ProductNameGlobal} ${item.ProductTypeNameGlobal} ${designText} ${measurement}`;
        const fastening = configuration && configuration.fastening ? configuration.fastening : fasteningType(item);

        const sink = {
          sinkId: makeCleanId(sinkId),
          displayName,
          item,
          configuration,
          width,
        };
        if (configListOfFastenings) {
          if (configListOfFastenings.length > 0) { 
            return createManuallyConfiguredSinksWithMoreThanOneFastening(sink, configListOfFastenings, item);
          }
        }
        const underGluedMaterialOptions = compareAndFilterMaterials(materialsUseDecentralizedSinkRange, materialsWithUnderGluing);
        if (underGluedMaterialOptions.length > 0 && fastening === fasteningTypes.topMounted) {
          return createSinksWithUnderGluedOperation(sink, underGluedMaterialOptions, sinkAllowsUnderGlued(item));
        } else {
          return [createSink(sink, item, fastening)];
        }
      }),
      groupBy('sinkId'),
      values,
      map((similarSinks) => {
        const sinksWithImages = similarSinks.filter(({ fastening }) => fastening !== 'underGlued');

        const displayImage = imgFinder('S3', 'Picture Single');
        const displayImageLarge = imgFinder('S4', 'Picture Single');
        const wood = imgFinderWithSortNo('S4', 'Functional Product Picture', 2);
        const light = imgFinderWithSortNo('S4', 'Functional Product Picture', 3);
        const dark = imgFinderWithSortNo('S4', 'Functional Product Picture', 4);

        const { configuration } = similarSinks.find((sink) => sink.configuration) || { };

        if (configuration && configuration.conditions) {
          const [thicknessConditions, depthConditions] = _.partition(configuration.conditions, (cond) => cond.thickness);

          // If you have more than one thickness or depth condition you are doing things wrong. so we only use the first one
          configuration.thicknessCondition = (thicknessConditions || []).map(processCondition)[0];
          configuration.depthCondition = (depthConditions || []).map(processCondition)[0];
        }

        return {
          id: similarSinks[0].sinkId,
          requiresTapHole: _.some(similarSinks, (ss) => sinkRequiresTapHole(ss.item)),
          hasDrainer: _.some(similarSinks, (ss) => sinkHasDrainer(ss.item)),
          displayName: similarSinks[0].displayName,
          displayImage: displayImage(sinksWithImages),
          displayImageLarge: displayImageLarge(sinksWithImages),
          source: similarSinks, // only for debug
          size: getSize(similarSinks[0].item),
          ProductName: similarSinks[0].item.ProductName,
          fastenings: _.groupBy(similarSinks, 'fastening'),
          woodImage: wood(sinksWithImages),
          lightImage: light(sinksWithImages),
          darkImage: dark(sinksWithImages),
          isManuallyConfigured: false,
          configuration,
        };
      }),
      concat(predefinedOptions.filter((option) => sinkRules.products.includes(option.source[0].item.ProductNameGlobal))),
      sortBy(productNameThenSize)
      )(items),
    };
  })
  .filter((sinkMaterial) => {
    if (sinkMaterial.models.length === 0) {
      // eslint-disable-next-line no-console
      console.log('Excluding sink material:', sinkMaterial.id, 'due to no models');
      return false;
    }

    return true;
  });
}
